import { useState, useEffect, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { workflowApi } from '../../services/api';
import { useProjectStore } from '../../store/useProjectStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import type { Project } from '../../types';

interface Props {
  onClose: () => void;
  project?: Project;
}

/** Aim for 1000 characters, do not enforce it.
 *
 * A hard limit on a prose field refuses the sentence someone is mid-way
 * through typing, so this counts and warns instead. The schema's 4000 is the
 * real ceiling and exists only to stop a paste putting an unbounded blob in a
 * project document. */
const PROSE_TARGET = 1000;

function CharCount({ value }: { value: string }) {
  const n = value.length;
  const over = n > PROSE_TARGET;
  return (
    <span
      className={`text-xs tabular-nums ${over ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-gray-500'}`}
      title={over ? `Over the ${PROSE_TARGET}-character guide — allowed, but consider trimming.` : undefined}
    >
      {n}/{PROSE_TARGET}
    </span>
  );
}

export default function CreateProjectModal({ onClose, project }: Props) {
  const { createProject, updateProject } = useProjectStore();
  const isEdit = !!project;
  const { organizations, programs, releaseTrains, fetchOrganizations, fetchPrograms, fetchReleaseTrains } = useOrgHierarchyStore();

  const [form, setForm] = useState({
    name: project?.name || '',
    description: project?.description || '',
    problemDetails: project?.problemDetails || '',
    technicalDescription: project?.technicalDescription || '',
    status: project?.status || 'planning',
    priority: project?.priority || 'medium',
    health: project?.health || 'on-track',
    startDate: project?.startDate || '',
    endDate: project?.endDate || '',
    owner: project?.owner || '',
    tags: project?.tags ? (Array.isArray(project.tags) ? project.tags.join(', ') : project.tags) : '',
    repositories: (project?.repositories || []).join(', '),
    defaultWorkflowId: project?.defaultWorkflowId || '',
    workflowBindings: (project?.workflowBindings || {}) as Record<string, string>,
    organizationId: project?.organizationId || '',
    programId: project?.programId || '',
    releaseTrainId: project?.releaseTrainId || '',
  });
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState<any[]>([]);

  useEffect(() => {
    workflowApi.list({ entityType: 'workitem' })
      .then(setWorkflows)
      .catch(() => setWorkflows([]));
  }, []);

  // Fetch orgs on mount
  useEffect(() => {
    fetchOrganizations();
    fetchPrograms();
    fetchReleaseTrains();
  }, []);

  // Filtered programs based on selected org
  const filteredPrograms = useMemo(() => {
    if (!form.organizationId) return programs;
    return programs.filter((p) => p.organizationId === form.organizationId);
  }, [programs, form.organizationId]);

  // Filtered release trains based on selected program
  const filteredReleaseTrains = useMemo(() => {
    if (!form.programId) return releaseTrains;
    return releaseTrains.filter((rt) => rt.programId === form.programId);
  }, [releaseTrains, form.programId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    if (!form.releaseTrainId) {
      toast.error('Release Train is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()) : [],
        repositories: form.repositories
          ? form.repositories.split(',').map((r) => r.trim()).filter(Boolean)
          : [],
        defaultWorkflowId: form.defaultWorkflowId,
        // Drop empty selections so "inherit" is stored as absence, not ''.
        workflowBindings: Object.fromEntries(
          Object.entries(form.workflowBindings).filter(([, v]) => v),
        ),
      } as any;
      if (isEdit && project) {
        await updateProject(project._id, payload);
        toast.success('Project updated');
      } else {
        await createProject(payload);
        toast.success('Project created');
      }
      onClose();
    } catch (err: any) {
      toast.error(err.message || (isEdit ? 'Failed to update project' : 'Failed to create project'));
    } finally {
      setLoading(false);
    }
  };

  // Accepts objects too: workflowBindings is a map, not a string.
  const update = (field: string, value: string | Record<string, string>) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const updateOrg = (orgId: string) => {
    setForm((prev) => ({ ...prev, organizationId: orgId, programId: '', releaseTrainId: '' }));
  };

  const updateProgram = (progId: string) => {
    // auto-set org from program if not already set
    const prog = programs.find((p) => p._id === progId);
    setForm((prev) => ({
      ...prev,
      programId: progId,
      releaseTrainId: '',
      organizationId: prog ? prog.organizationId : prev.organizationId,
    }));
  };

  const updateReleaseTrain = (rtId: string) => {
    // auto-set program and org from release train
    const rt = releaseTrains.find((r) => r._id === rtId);
    if (rt) {
      const prog = programs.find((p) => p._id === rt.programId);
      setForm((prev) => ({
        ...prev,
        releaseTrainId: rtId,
        programId: rt.programId,
        organizationId: rt.organizationId || (prog ? prog.organizationId : prev.organizationId),
      }));
    } else {
      setForm((prev) => ({ ...prev, releaseTrainId: rtId }));
    }
  };

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {isEdit ? 'Edit Project' : 'Create New Project'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X size={18} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Project Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      placeholder="e.g., Website Redesign"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      rows={3}
                      placeholder="Brief description of the project..."
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      The <strong>what</strong> — what this project is.
                    </p>
                  </div>

                  {/* The why and the how. Kept as separate fields rather than
                      folded into the description: when there is only one box,
                      the "why" is the part that never gets written down. */}
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Problem Details
                      </label>
                      <CharCount value={form.problemDetails} />
                    </div>
                    <textarea
                      value={form.problemDetails}
                      onChange={(e) => update('problemDetails', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      rows={5}
                      placeholder="What problem does this solve, and what happens if it is not solved? Who feels the pain today, and how are they working around it?"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      The <strong>why</strong> — why this product or project needs to exist.
                    </p>
                  </div>

                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Technical Description
                      </label>
                      <CharCount value={form.technicalDescription} />
                    </div>
                    <textarea
                      value={form.technicalDescription}
                      onChange={(e) => update('technicalDescription', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      rows={5}
                      placeholder="Architecture, language and framework, where it runs, what it stores, and how it fits with the other products and applications on the estate."
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      The <strong>how</strong> — architecture, tools, and how this fits the estate.
                    </p>
                  </div>

                  {/* SAFe Hierarchy: Organization → Program → Release Train */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-700/50">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">SAFe Hierarchy</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Organization</label>
                        <select
                          value={form.organizationId}
                          onChange={(e) => updateOrg(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-accent/50"
                        >
                          <option value="">— Select —</option>
                          {organizations.map((o) => (
                            <option key={o._id} value={o._id}>{o.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Program</label>
                        <select
                          value={form.programId}
                          onChange={(e) => updateProgram(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-accent/50"
                        >
                          <option value="">— Select —</option>
                          {filteredPrograms.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Release Train <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={form.releaseTrainId}
                          onChange={(e) => updateReleaseTrain(e.target.value)}
                          className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-accent/50"
                        >
                          <option value="">— Select —</option>
                          {filteredReleaseTrains.map((rt) => (
                            <option key={rt._id} value={rt._id}>{rt.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={form.status}
                        onChange={(e) => update('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="planning">Planning</option>
                        <option value="active">Active</option>
                        <option value="on-hold">On Hold</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                      <select
                        value={form.priority}
                        onChange={(e) => update('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={form.startDate}
                        onChange={(e) => update('startDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                      <input
                        type="date"
                        value={form.endDate}
                        onChange={(e) => update('endDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</label>
                    <input
                      type="text"
                      value={form.owner}
                      onChange={(e) => update('owner', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder="Project owner name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={form.tags}
                      onChange={(e) => update('tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder="frontend, design, phase-1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Repositories <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated owner/name)</span>
                    </label>
                    <input
                      type="text"
                      value={form.repositories}
                      onChange={(e) => update('repositories', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder="Heuristic-Systems/PMO"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Links commits and pull requests to this project's work items.
                      Work-item ids are only unique within a project, so this mapping
                      is what makes a reference like BG-12 resolvable.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Workflow
                    </label>
                    <select
                      value={form.defaultWorkflowId}
                      onChange={(e) => update('defaultWorkflowId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                    >
                      <option value="">Inherit (program, then organisation, then Default)</option>
                      {workflows.map((w) => (
                        <option key={w._id} value={w._id}>
                          {w.name}{w.isDefault ? ' (global default)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Governs every work item type in this project unless overridden
                      below. Changing it does not move existing items: each one keeps
                      the workflow and version it was created under.
                    </p>

                    <details className="mt-2">
                      <summary className="text-xs text-accent cursor-pointer">
                        Override per work item type
                      </summary>
                      <div className="mt-2 space-y-1.5">
                        {['epic', 'feature', 'story', 'task', 'bug', 'security'].map((wit) => (
                          <div key={wit} className="flex items-center gap-2">
                            <span className="w-16 text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {wit}
                            </span>
                            <select
                              value={form.workflowBindings[wit] || ''}
                              onChange={(e) => update('workflowBindings', {
                                ...form.workflowBindings, [wit]: e.target.value,
                              })}
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                            >
                              <option value="">Use the project workflow</option>
                              {workflows.map((w) => (
                                <option key={w._id} value={w._id}>{w.name}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Bugs and features often need different states, which is why
                          this exists. Leave everything blank for one workflow per project.
                        </p>
                      </div>
                    </details>
                  </div>

                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !form.releaseTrainId}
                      className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors"
                    >
                      {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Project')}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
